import {useIsomorphicLayoutEffect} from '@aboutyou/ui-components';
import React, {useCallback, useState} from 'react';
import {Rect, useRectEffect} from 'react-viewport-utils';
import {
  NAVIGATION_HEIGHT,
  SEARCH_BAR_FOCUSED_WIDTH,
  SEARCH_BAR_OVERLAY_TOP_CSS_VAR,
  SEARCH_BAR_PINNED_TOP,
} from '../../constants';
import {SearchBarOpenOrigin} from './useSearchBarDisplayState';

// Only the primitives the render needs. An unchanged measurement is handed
// back as the same object, so the collector's frame after a scroll or an open
// does not re-render the bar and the panel below it for nothing.
interface Measurement {
  left: number;
  viewportWidth: number;
  reachesPinnedTop: boolean;
}

const getViewportWidth = () => document.documentElement.clientWidth;

export const useExpandToCenter = ({
  searchWrapperRef,
  expandToCenter,
  isSearchBarExtended,
  openOrigin,
}: {
  searchWrapperRef: React.RefObject<HTMLDivElement | null>;
  expandToCenter?: boolean;
  isSearchBarExtended: boolean;
  openOrigin?: SearchBarOpenOrigin | null;
}) => {
  const canExpandToCenter = Boolean(expandToCenter);
  const isActive = canExpandToCenter && isSearchBarExtended;

  const [measurement, setMeasurement] = useState<Measurement | null>(null);

  const applyRect = useCallback(
    (rect: Rect | null) => {
      if (!rect) {
        return;
      }
      const reachesPinnedTop = rect.top <= SEARCH_BAR_PINNED_TOP;
      const barTop = reachesPinnedTop ? SEARCH_BAR_PINNED_TOP : rect.top;

      searchWrapperRef.current?.style.setProperty(
        SEARCH_BAR_OVERLAY_TOP_CSS_VAR,
        `${barTop + rect.height / 2 + NAVIGATION_HEIGHT / 2}px`,
      );

      const viewportWidth = getViewportWidth();

      setMeasurement(previous =>
        previous &&
        previous.left === rect.left &&
        previous.viewportWidth === viewportWidth &&
        previous.reachesPinnedTop === reachesPinnedTop
          ? previous
          : {left: rect.left, viewportWidth, reachesPinnedTop},
      );
    },
    [searchWrapperRef],
  );

  useRectEffect(
    applyRect,
    searchWrapperRef,
    {
      disableScrollUpdates: !isActive,
      priority: isActive ? 'highest' : 'low',
    },
    [isActive, applyRect],
  );

  // The collector reports on its next frame. Measuring on open makes the first
  // painted frame of the open bar correct already, and if nothing moved since
  // the last report there is no follow-up render.
  useIsomorphicLayoutEffect(() => {
    if (isActive) {
      applyRect(searchWrapperRef.current?.getBoundingClientRect() ?? null);
    }
  }, [isActive, applyRect, searchWrapperRef]);

  const centerOffset = measurement
    ? measurement.viewportWidth / 2 -
      (measurement.left + SEARCH_BAR_FOCUSED_WIDTH / 2)
    : 0;

  // The bar keeps the left edge of its own row, so the sticky icon it was
  // opened from is expressed as a translation away from that edge.
  const originOffset =
    openOrigin && measurement ? openOrigin.left - measurement.left : 0;

  return {
    canExpandToCenter,
    originOffset,
    originWidth: openOrigin?.width ?? 0,
    shouldExpandToCenter: isActive,
    hasMeasurement: measurement !== null,
    centerOffset,
    isPinned: Boolean(isActive && measurement?.reachesPinnedTop),
  };
};
